import {clsx} from 'clsx'
import safeStorage from '@github-ui/safe-storage'
import {UnderlineNav} from '@primer/react'
import type React from 'react'
import {useCallback, useEffect, useEffectEvent, useState, type ReactNode} from 'react'
import {CodespacesTabContent, CodespacesTabWrapper} from '@github-ui/codespaces-tab/components/CodespacesTab'
import {RefInfoProvider} from '@github-ui/code-view-shared/contexts/RefInfoContext'
import {PathProvider} from '@github-ui/code-view-shared/contexts/PathContext'
import {ActionProvider} from '@github-ui/code-view-shared/contexts/ActionContext'
import {EditPermissionsProvider} from '@github-ui/code-view-shared/contexts/EditPermissionsContext'
import {CopilotSettingsProvider} from '@github-ui/code-view-shared/contexts/CopilotSettingsContext'
import type {RefInfo} from '@github-ui/repos-types'

import {useCodeButtonData} from '@github-ui/pull-requests/page-data/loaders/use-code-button-data'
import {CodeMenuButton, type CodeMenuButtonProps} from './CodeMenuButton'
import styles from './CodeDropdownButton.module.css'

const safeLocalStorage = safeStorage('localStorage')

export interface CodeDropdownButtonProps {
  className?: string
  size?: 'small' | 'large' | 'medium'
  showCodespacesTab?: boolean
  isEnterprise: boolean
  localTab: ReactNode
  codespacesTab?: ReactNode
  codespacesPath?: string
  /** Branch or ref name for the code button (e.g., PR head branch) */
  refName: string
  refType?: RefInfo['refType']
  currentBranchName?: string
  createRef?: string
  iconOnly?: boolean
  variant?: CodeMenuButtonProps['variant']
  trailingVisual?: boolean
  onMenuOpen?: () => void
  onTabSelect?: (tab: 'local' | 'codespaces') => void
}

const ActiveTab = {
  Local: 'local',
  Codespaces: 'cloud',
  Copilot: 'copilot',
} as const

type ActiveTab = (typeof ActiveTab)[keyof typeof ActiveTab]

export function CodeDropdownButton(props: CodeDropdownButtonProps) {
  const {
    className,
    size,
    showCodespacesTab,
    isEnterprise,
    localTab,
    codespacesTab,
    codespacesPath,
    refName,
    refType,
    currentBranchName,
    createRef,
    iconOnly,
    variant,
    trailingVisual,
    onMenuOpen,
    onTabSelect,
  } = props
  const localStorageDefaultTabKey = 'code-button-default-tab'
  const [activeTab, setActiveTab] = useState<string>(ActiveTab.Local)
  const showTabNav = !isEnterprise && showCodespacesTab

  // Create RefInfo for context providers
  const refInfo: RefInfo = {
    name: refName,
    listCacheKey: refName,
    refType: refType ?? 'branch',
    currentOid: '', // Not needed for code button functionality
    canEdit: false,
    canEditOnDefaultBranch: false,
    fileExistsOnDefault: false,
  }

  const onCodespacesTabClick = useCallback(
    (ev?: React.MouseEvent) => {
      setActiveTab(ActiveTab.Codespaces)
      safeLocalStorage.setItem(localStorageDefaultTabKey, ActiveTab.Codespaces)
      onTabSelect?.('codespaces')
      ev?.preventDefault()
    },
    [onTabSelect],
  )

  const onLocalTabClick = useCallback(
    (ev?: React.MouseEvent) => {
      setActiveTab(ActiveTab.Local)
      safeLocalStorage.setItem(localStorageDefaultTabKey, ActiveTab.Local)
      onTabSelect?.('local')
      ev?.preventDefault()
    },
    [onTabSelect],
  )

  const handleInitialTabSelect = useEffectEvent(() => {
    const defaultActiveTab = safeLocalStorage.getItem(localStorageDefaultTabKey)
    if (defaultActiveTab === ActiveTab.Codespaces && showCodespacesTab) {
      // Restore persisted tab without emitting user-interaction analytics.
      setActiveTab(ActiveTab.Codespaces)
      safeLocalStorage.setItem(localStorageDefaultTabKey, ActiveTab.Codespaces)
    }
  })

  useEffect(() => {
    // Only run after the initial render.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    handleInitialTabSelect()
  }, [])

  return (
    <PathProvider path="">
      <ActionProvider action="tree">
        <CopilotSettingsProvider copilotAccessAllowed={false}>
          <RefInfoProvider refInfo={refInfo}>
            <EditPermissionsProvider canEdit={false} canEditOnDefaultBranch={false} fileExistsOnDefault={false}>
              <CodeMenuButton
                size={size}
                className={className}
                codespacesPath={codespacesPath}
                shouldPrefetchCodespaces={Boolean(showCodespacesTab)}
                iconOnly={iconOnly}
                trailingVisual={trailingVisual}
                variant={variant}
                onMenuOpen={onMenuOpen}
              >
                {showTabNav && (
                  <UnderlineNav aria-label="Code access options" className={styles.UnderlineNav}>
                    <UnderlineNav.Item
                      as="button"
                      type="button"
                      aria-current={activeTab === ActiveTab.Local ? 'page' : undefined}
                      onClick={onLocalTabClick}
                    >
                      Local
                    </UnderlineNav.Item>
                    <UnderlineNav.Item
                      as="button"
                      type="button"
                      aria-current={activeTab === ActiveTab.Codespaces ? 'page' : undefined}
                      onClick={onCodespacesTabClick}
                    >
                      Codespaces
                    </UnderlineNav.Item>
                  </UnderlineNav>
                )}
                <div className={clsx(styles.ActionList, 'py-0')}>
                  {activeTab === ActiveTab.Local && localTab}
                  {activeTab === ActiveTab.Codespaces &&
                    (codespacesTab || renderCodespacesTab(codespacesPath, currentBranchName, createRef))}
                </div>
              </CodeMenuButton>
            </EditPermissionsProvider>
          </RefInfoProvider>
        </CopilotSettingsProvider>
      </ActionProvider>
    </PathProvider>
  )
}

function renderCodespacesTab(codespacesPath?: string, currentBranchName?: string, createRef?: string) {
  return (
    <CodespacesTabWrapper>
      <SuspendedCodespacesTab
        codespacesPath={codespacesPath}
        currentBranchName={currentBranchName}
        createRef={createRef}
      />
    </CodespacesTabWrapper>
  )
}

function SuspendedCodespacesTab({
  codespacesPath,
  currentBranchName,
  createRef,
}: {
  codespacesPath?: string
  currentBranchName?: string
  createRef?: string
}) {
  const data = useCodeButtonData().data
  return (
    <CodespacesTabContent
      {...data}
      codespacesPath={codespacesPath}
      currentBranchName={currentBranchName}
      createRef={createRef}
    />
  )
}
